import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import CountUp from "react-countup";
import { FaRegBuilding } from "react-icons/fa";
import PieChart from "./components/PieChart";
import YtdDetails from "./components/YtdDetails";
import axios from "axios";
import DashboardControls from "./components/DashboardControls";
import { useHistory, useLocation } from "react-router-dom";
import WeeklyChart from "./components/WeeklyChart";

// Utility function to format cents to dollars
const formatCurrency = (amount: number | null) => {
  return amount !== null ? `$${(amount / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "$0.00";
};

const Production = () => {
  const location = useLocation();
  const history = useHistory();

  const getParams = () => {
    const params = new URLSearchParams(location.search);
    return {
      divisionId: params.get("divisionId") ? Number(params.get("divisionId")) : null,
      date: params.get("date") ? new Date(params.get("date")!) : new Date(),
    };
  };

  const [selectedDivision, setSelectedDivision] = useState<number | null>(getParams().divisionId);
  const [selectedDate, setSelectedDate] = useState<Date | null>(getParams().date);
  const formattedDate = format(selectedDate || new Date(), "yyyy-MM-dd");

  const [currentTime, setCurrentTime] = useState(
    new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true })
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12: true }));
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  // Default company dashboard state
  interface Division {
    division_id: number;
    division_label: string;
    invoice_total_yesterday: number;
    invoice_total_ytd: number;
    invoice_total_ytd_previous: number;
  }

  const [companyDashboardData, setCompanyDashboardData] = useState<{
    date: string;
    invoiceTotalYtd: number;
    invoiceTotalYtdPrevious: number;
    invoiceTotalYesterday: number;
    companyGoal: number;
    divisions: Division[];
    weeklyProduction: any[];
  }>({
    date: "",
    invoiceTotalYtd: 0,
    invoiceTotalYtdPrevious: 0,
    invoiceTotalYesterday: 0,
    companyGoal: 0,
    divisions: [],
    weeklyProduction: [],
  });

  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    if (!formattedDate) return;

    const endpoint = selectedDivision
      ? `${process.env.REACT_APP_SERVER_URL}/api/v1/company/production/division-dashboard-daily`
      : `${process.env.REACT_APP_SERVER_URL}/api/v1/company/production/company-dashboard-daily`;

    const params = { date: formattedDate, ...(selectedDivision && { divisionId: selectedDivision }) };

    axios
      .get(endpoint, { params })
      .then((response) => {
        if (selectedDivision) {
          // Division selected - handle individual breakdown

          setIndividualsData(response.data || []);
        } else {
          // No division - handle company overview
    
          setCompanyDashboardData({
            date: response.data.date,
            invoiceTotalYtd: response.data.invoice_total_ytd || 0,
            invoiceTotalYtdPrevious: response.data.invoice_total_ytd_previous || 0,
            invoiceTotalYesterday: response.data.invoice_total_yesterday || 0,
            companyGoal: response.data.company_goal || 0,
            divisions: response.data.divisions || [],
            weeklyProduction: response.data.weekly_data || [],
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching dashboard data", error);
      });
  }, [formattedDate, selectedDivision]);

  const [individualsData, setIndividualsData] = useState<any[]>([]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (selectedDivision !== null) {
      params.set("divisionId", selectedDivision.toString());
    } else {
      params.delete("divisionId");
    }

    if (selectedDate) {
      params.set("date", format(selectedDate, "yyyy-MM-dd"));
    } else {
      params.delete("date");
    }

    history.replace({ search: params.toString() });
  }, [selectedDivision, selectedDate, history, location.search]);

  const [selectedPriceBasis, setSelectedPriceBasis] = useState("Invoiced");
  const [autoCycleEnabled, setAutoCycleEnabled] = useState(true);

  // Calculate progress percentage safely
  const progressPercent =
    companyDashboardData.companyGoal > 0
      ? (companyDashboardData.invoiceTotalYtd / companyDashboardData.companyGoal) * 100
      : 0;

  // Toggle dropdown visibility
  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  // Handle division selection
  const handleDivisionSelect = (divisionId: number | null) => {
    setSelectedDivision(divisionId);
    setDropdownOpen(false);
  };

  // Check if company overview is selected
  const isCompanyOverview = selectedDivision === null;

  // Inside the Production component
  const handleRefresh = () => {
    

    axios.post(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/production/populate-daily-production`, {
      date: formattedDate
    })
      .then(() => {
        // After successful populate, refetch the company dashboard data
        return axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/production/company-dashboard-daily`, {
          params: { date: formattedDate }
        });
      })
      .then(response => {
        
        setCompanyDashboardData({
          date: response.data.date,
          invoiceTotalYtd: response.data.invoice_total_ytd || 0,
          invoiceTotalYtdPrevious: response.data.invoice_total_ytd_previous || 0,
          invoiceTotalYesterday: response.data.invoice_total_yesterday || 0,
          companyGoal: response.data.company_goal || 0,
          divisions: response.data.divisions || [],
          weeklyProduction: response.data.weekly_data || [],
        });
        if (selectedDivision) {
          // Division selected - handle individual breakdown
          return axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/production/division-dashboard-daily`, {
            params: { date: formattedDate, divisionId: selectedDivision }
          }).then(response => {
            
            setIndividualsData(response.data || []);
          });
        }
      })
      .catch(error => {
        console.error("Error refreshing data", error);
      });
  };

  const handleFilterSelect = (filter: string) => {
   
    // Logic to apply the selected filter
  };

  useEffect(() => {
    if (!autoCycleEnabled || companyDashboardData.divisions.length === 0) return;

    const cycleInterval = setInterval(() => {
      setSelectedDivision((prev: any) => {
        if (prev === null) return companyDashboardData.divisions[0].division_id;
        const currentIndex = companyDashboardData.divisions.findIndex(
          (d: any) => d.division_id === prev
        );
        const nextIndex =
          (currentIndex + 1) % (companyDashboardData.divisions.length + 1);
        return nextIndex === companyDashboardData.divisions.length
          ? null
          : companyDashboardData.divisions[nextIndex].division_id;
      });
    }, 5000);

    return () => clearInterval(cycleInterval);
  }, [companyDashboardData.divisions, autoCycleEnabled]);

  return (
    <div className="min-h-screen bg-[#25292E] text-white flex flex-col">
      {/* Header */}
      <div className="flex justify-between items-center p-4 bg-[#30343A]" style={{ height: 50 }}>
        <div className="flex items-center">
          <h1 className="text-lg font-bold">Production</h1>
        </div>

        {/* Company/Division Dropdown */}
        <div className="relative">
          <button
            onClick={toggleDropdown}
            className="flex items-center px-4 py-2 rounded-full text-white hover:bg-gray-600"
          >
            <FaRegBuilding className="mr-2" />
            <span>
              {/* @ts-ignore */}
              {isCompanyOverview
                ? "Company Overview"
                // @ts-ignore
                : companyDashboardData.divisions.find((d: any) => d.division_id === selectedDivision)?.division_label || "Unknown Division"}
            </span>
          </button>
          {dropdownOpen && (
            <div className="absolute left-0 mt-2 bg-gray-800 text-white rounded shadow-lg w-48 z-50">
              <button
                onClick={() => handleDivisionSelect(null)}
                className="block w-full px-4 py-2 text-left hover:bg-gray-700"
              >
                Company Overview
              </button>
              {companyDashboardData.divisions.map((division: any) => (
                <button
                  key={division.division_id}
                  onClick={() => handleDivisionSelect(division.division_id)}
                  className="block w-full px-4 py-2 text-left hover:bg-gray-700"
                >
                  {division.division_label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center">
          <DashboardControls
            onRefresh={handleRefresh}
            onFilterSelect={handleFilterSelect}
            autoCycleEnabled={autoCycleEnabled}
            setAutoCycleEnabled={setAutoCycleEnabled}
            selectedPriceBasis={selectedPriceBasis}
            setSelectedPriceBasis={setSelectedPriceBasis}
          />

          {/* Date Picker + Time Pill */}
          <div className="h-10 bg-gray-600 flex rounded-full text-sm w-[160px] items-center justify-between px-4">

            {/* Date Picker */}
            <DatePicker
              selected={selectedDate}
              onChange={(date: Date | null) => setSelectedDate(date)}
              dateFormat="M/d/yy"
              className="bg-transparent text-white cursor-pointer outline-none text-center w-[58px]"
            />

            {/* Time Display */}
            <div className="text-white font-semibold">{currentTime}</div>
          </div>

        </div>
      </div>

      {/* Table Section */}
      <div className="bg-[#30343A] ml-2 mr-2 mt-2 p-4 rounded overflow-auto flex-1">
        <div className="grid grid-cols-5 font-semibold text-sm border-b border-gray-700 pb-2 text-[#23f0b6]">
          <div>Division</div>
          <div>Yesterday Actual</div>
          <div>Year To Date</div>
          <div>Last Year To Date</div>
          <div>Goal Progress</div>
        </div>
        <div className="mt-2 space-y-2 text-sm">
          {isCompanyOverview ? (
            // Show Divisions Table
            companyDashboardData.divisions.length > 0 ? (
              companyDashboardData.divisions.map((division: any) => (
                <div className="grid grid-cols-5" key={division.division_id}>
                  <div>{division.division_label}</div>
                  <div>{formatCurrency(division.invoice_total_yesterday)}</div>
                  <div>{formatCurrency(division.invoice_total_ytd)}</div>
                  <div>{formatCurrency(division.invoice_total_ytd_previous)}</div>
                  <div className="text-green-500">
                    {companyDashboardData.companyGoal > 0
                      ? `${((division.invoice_total_ytd / companyDashboardData.companyGoal) * 100).toFixed(2)}%`
                      : "0.00%"}
                  </div>
                </div>
              ))
            ) : (
              <div>No Division Data Found...</div>
            )
          ) : (
            // Show Individuals Table when a Division is Selected
            individualsData.length > 0 ? (
              individualsData.map((individual: any) => (
                <div className="grid grid-cols-5" key={individual.user_id}>
                  <div>{individual.user_name}</div>
                  <div>{formatCurrency(individual.invoice_total_yesterday)}</div>
                  <div>{formatCurrency(individual.invoice_total_ytd)}</div>
                  <div>{formatCurrency(individual.invoice_total_ytd_previous)}</div>
                  <div className="text-green-500">
                    {companyDashboardData.companyGoal > 0
                      ? `${((individual.invoice_total_ytd / companyDashboardData.companyGoal) * 100).toFixed(2)}%`
                      : "0.00%"}
                  </div>
                </div>
              ))
            ) : (
              <div>No Individual Data Found...</div>
            )
          )}
        </div>

      </div>

      {/* Goal and Progress Section */}
      <div className="grid grid-cols-3 gap-2 bg-[#25292E] m-2">
        <div className="bg-[#30343A] rounded flex items-center justify-center ">
          <div className="text-green-500 text-4xl">🏆</div>
          <div className="text-lg font-bold ml-4">
            Goal: {formatCurrency(companyDashboardData.companyGoal)}
          </div>
        </div>
        <div className="col-span-2 bg-[#30343A] p-4 rounded flex items-center">
          <div className="w-full bg-gray-700 rounded-full h-6">
            <div
              className="bg-green-500 h-6 rounded-full"
              style={{ width: `${progressPercent}%` }}
            ></div>
          </div>
          <div className="ml-4 text-lg font-bold">
            <CountUp end={progressPercent} duration={1} decimals={1} />%
          </div>
        </div>
      </div>

      {/* Chart Section */}
      <div className="mr-2 ml-2 mb-2">
        <div className="grid grid-cols-3 gap-2">
          {/* Line Chart */}
        
          <WeeklyChart weeklyProduction={
            companyDashboardData.weeklyProduction
          } />
          <div className="flex flex-row">
            <div className="bg-[#30343A] p-4 rounded w-[50%]">
              <PieChart companyDashboardData={companyDashboardData} />
            </div>
            <div className="bg-[#30343A] p-4 rounded w-[50%]">
              <YtdDetails companyDashboardData={companyDashboardData} />
            </div>
          </div>
        </div>
      </div>
    </div >
  );
};

export default Production;